'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { ExclamationTriangleIcon, XCircleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

export default function InviteCard({ organizationId, onInviteSuccess }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Member');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  
  // State from organization_subscriptions view
  const [organizationData, setOrganizationData] = useState(null);
  const [canInviteMembers, setCanInviteMembers] = useState(true);
  const [memberUsagePercent, setMemberUsagePercent] = useState(0);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Fetch organization data from the view
  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        if (!organizationId) return;
        
        // Use the organization_subscriptions view
        const { data: orgData, error: orgError } = await supabase
          .from('organization_subscriptions')
          .select('*')
          .eq('id', organizationId)
          .single();

        if (orgError) throw orgError;

        setOrganizationData(orgData);

        // Calculate usage and limits - view handles trial limits (9999 during trial)
        const currentMembers = orgData.current_member_count || 0;
        const maxMembers = orgData.max_member_count || 0;

        // Calculate usage percentage
        const memberPercent = maxMembers > 0 ? (currentMembers / maxMembers) * 100 : 0;
        setMemberUsagePercent(memberPercent);

        // Determine if they can invite more
        setCanInviteMembers(currentMembers < maxMembers);

      } catch (error) {
        console.error('Error fetching organization data:', error);
        setError('Failed to load organization data');
      } finally {
        setLoadingData(false);
      }
    };

    fetchOrganizationData();
  }, [organizationId]);

  const handleSendInvite = async (e) => {
    if (e) e.preventDefault();
  
    if (!validateEmail(email)) {
      setNotification({
        type: 'error',
        message: 'Must be a valid email address',
      });
      return;
    }
  
    // Check limits before sending invite
    if (!canInviteMembers) {
      setNotification({
        type: 'error',
        message: 'You\'ve reached your member limit. Upgrade to add more members.',
      });
      return;
    }
  
    setError('');
    setLoading(true);
  
    try {
      // Check if user already exists in an organization
      const { data: profiles, error: profileError } = await supabase
        .from('profile')
        .select('id')
        .eq('email_address', email);
  
      if (profileError) {
        throw new Error('Failed to fetch profile information');
      }
  
      if (profiles && profiles.length > 0) {
        const profileId = profiles[0].id;
        const { data: memberships, error: membershipError } = await supabase
          .from('organization_members')
          .select('id')
          .eq('user_id', profileId);
  
        if (membershipError) {
          throw new Error('Failed to check existing membership');
        }
  
        if (memberships && memberships.length > 0) {
          throw new Error('User is already part of an organization');
        }
      }
  
      // NEW: Check for existing pending invites to this organization
      const { data: existingInvites, error: inviteCheckError } = await supabase
        .from('organization_invites')
        .select('id')
        .eq('email', email)
        .eq('organization_id', organizationId)
        .eq('status', 'pending');
  
      if (inviteCheckError) {
        throw new Error('Failed to check existing invitations');
      }
  
      if (existingInvites && existingInvites.length > 0) {
        throw new Error('This user already has a pending invitation to your organization');
      }
  
      // Create the invite
      const { data: invite, error: createError } = await supabase
        .from('organization_invites')
        .insert({
          organization_id: organizationId,
          email,
          role,
          status: 'pending',
        })
        .select('id, token')
        .single();
  
      if (createError || !invite) {
        // Handle specific constraint violation errors
        if (createError?.code === '23505' || createError?.message?.includes('unique')) {
          throw new Error('This user already has a pending invitation to your organization');
        }
        throw new Error('Failed to create invite');
      }
  
      // Send invitation email
      const response = await fetch(
        'https://nodejs-serverless-function-express-azure-delta.vercel.app/api/send_invitation_email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inviteId: invite.id,
            email,
            role,
          }),
        }
      );
  
      const emailResult = await response.json();
  
      if (!response.ok) {
        throw new Error(emailResult.error || 'Failed to send invitation email');
      }
  
      // Success
      onInviteSuccess();
      setNotification({
        type: 'success',
        message: `Invitation sent successfully to ${email}`,
      });
      setEmail('');
      setRole('Member');
  
    } catch (err) {
      console.error('Error sending invite:', err);
      setNotification({
        type: 'error',
        message: err.message || 'Failed to send invitation',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusMessage = () => {
    if (!organizationData) return null;

    const isTrialing = organizationData.subscription_status === 'trialing';
    const trialEndDate = organizationData.trial_end_date ? new Date(organizationData.trial_end_date) : null;
    const trialActive = isTrialing && trialEndDate && trialEndDate > new Date();

    if (trialActive) {
      const daysLeft = Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24));
      return {
        type: 'info',
        message: `Trial active: ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining. Unlimited team invites during trial.`
      };
    }

    if (!canInviteMembers) {
      return {
        type: 'warning',
        message: `You've reached your member limit (${organizationData.max_member_count} Max). Upgrade to add more members.`
      };
    }

    // Only show usage percentage for paid plans (not during trial with 9999 limit)
    if (memberUsagePercent >= 80 && organizationData.max_member_count < 9999) {
      return {
        type: 'info',
        message: `You're using ${organizationData.current_member_count} of ${organizationData.max_member_count} members (${Math.round(memberUsagePercent)}%).`
      };
    }

    return null;
  };

  if (loadingData) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-gray-300 shadow-sm mb-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-300 rounded w-1/2"></div>
          <div className="h-4 bg-gray-300 rounded w-full"></div>
          <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  const statusMessage = getStatusMessage();

  return (
    <div className="mb-4">
      {/* Notification */}
      <div
        aria-live="assertive"
        className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-50"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {notification && (
            <div
              className={`max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden transform transition-all ${
                notification.type === 'error' ? 'border-red-400' : 'border-green-400'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {notification.type === 'error' ? (
                      <XCircleIcon className="h-6 w-6 text-red-400" />
                    ) : (
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <p
                      className={`text-sm font-medium ${
                        notification.type === 'error' ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      {notification.message}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setNotification(null)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div className={`border-l-4 p-4 mb-4 ${
          statusMessage.type === 'warning' 
            ? 'border-red-400 bg-red-50' 
            : 'border-blue-400 bg-blue-50'
        }`}>
          <div className="flex">
            <div className="shrink-0">
              {statusMessage.type === 'warning' ? (
                <ExclamationTriangleIcon aria-hidden="true" className="h-5 w-5 text-red-400" />
              ) : (
                <CheckCircleIcon aria-hidden="true" className="h-5 w-5 text-blue-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm ${
                statusMessage.type === 'warning' ? 'text-red-700' : 'text-blue-700'
              }`}>
                {statusMessage.message}
                {statusMessage.type === 'warning' && (
                  <>
                    {' '}
                    <a
                      href="/settings/Billing"
                      className="font-medium underline hover:opacity-80"
                    >
                      Upgrade your subscription
                    </a>
                    {' to add more members.'}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invite Form - Show unless at member limit */}
      {canInviteMembers && (
        <div className="relative p-4 rounded-lg bg-gray-50 border border-gray-300 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Invite a Team Member</h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
                required
              />
            </div>

            <div>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
              >
                <option value="Member">Member</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            <div>
              <button
                type="button"
                onClick={() => handleSendInvite()}
                disabled={loading}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white ${
                  loading ? 'bg-gray-400' : 'bg-yellow-600 hover:bg-yellow-500'
                } rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500`}
              >
                {loading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}